import type { ThunkDispatch } from '@reduxjs/toolkit';
import type { AnyAction } from 'redux';
import type Customer from 'types/customer';
import { deserializeCustomer, makeCustomer } from 'types/customer';
import { addCustomer, updateCustomer } from 'store/customers/customersSlice';
import type { RootState } from 'store';
import type { ImportAdapter, ImportColumnDef, ImportRowAnalysis } from 'ui-component/import-wizard';
import { applyPatch, CLIENT_COL, findClient, rowToPartial } from './importClientsFromXlsxUi';

type AppThunkDispatch = ThunkDispatch<RootState, unknown, AnyAction>;

const str = (key: keyof typeof CLIENT_COL, required = false): ImportColumnDef => ({ key, label: CLIENT_COL[key], required, type: 'string' });
const num = (key: keyof typeof CLIENT_COL): ImportColumnDef => ({ key, label: CLIENT_COL[key], type: 'number' });
const bool = (key: keyof typeof CLIENT_COL): ImportColumnDef => ({ key, label: CLIENT_COL[key], type: 'boolean' });

export const CLIENTS_IMPORT_COLUMNS: ImportColumnDef[] = [
    str('name', true),
    str('businessId'),
    str('businessName'),
    bool('includeTax'),
    str('city'),
    num('internalId'),
    num('active'),
    str('agent'),
    str('gps'),
    str('driver'),
    str('phone2'),
    str('contact2'),
    str('phone3'),
    str('contact3'),
    str('street'),
    str('streetNumber'),
    str('phone'),
    str('contact'),
    num('priceControl'),
    str('email'),
    num('taxNoteType'),
    num('discount'),
    str('externalId'),
    num('position'),
    str('comments'),
    num('minOrder'),
    str('category'),
    num('master'),
    str('paymentComments'),
    num('branch'),
    str('group'),
    num('obligo'),
    str('days', false),
    num('minOrderMoney'),
    num('daysToPay')
];
CLIENTS_IMPORT_COLUMNS.find((c) => c.key === 'internalId')!.hint = 'מזהה פנימי במערכת — אם מולא, שורה זו תעדכן לקוח קיים עם מזהה זה';
CLIENTS_IMPORT_COLUMNS.find((c) => c.key === 'externalId')!.hint = 'מזהה מול מערכת חיצונית — משמש גם הוא להתאמת לקוח קיים';
CLIENTS_IMPORT_COLUMNS.find((c) => c.key === 'days')!.hint = 'ימי אספקה: מספר ביט או אותיות א-ש מופרדות בפסיק';

export const CLIENTS_FIELD_LABELS: Record<string, string> = {
    name: 'שם לקוח',
    businessId: 'מס עוסק',
    businessName: 'שם חברה',
    includeTax: 'כולל מעמ',
    city: 'עיר',
    active: 'פעיל',
    agent: 'סוכן',
    location: 'נקודת ציון',
    driver: 'נהג',
    phone2: 'טלפון 2',
    contactName2: 'איש קשר 2',
    phone3: 'טלפון 3',
    contactName3: 'איש קשר 3',
    street: 'רחוב',
    streetNumber: 'מספר רחוב',
    phone: 'טלפון',
    contactName: 'איש קשר',
    priceControl: 'מחירון',
    email: 'אימייל',
    tax_note_type: 'תעודות',
    discount: 'הנחה קבועה',
    externalId: 'מזהה חיצוני',
    position: 'מיקום',
    comments: 'הערות',
    min_order: 'מינימום',
    category: 'קטגוריה',
    master: 'מרכזי',
    paymentComments: 'תנאי תשלום',
    branch: 'סניף',
    group: 'קטגוריה 2',
    obligo: 'אובליגו',
    min_order_sum: 'מינימום הזמנה בכסף',
    days_to_pay: 'שוטף',
    sunday: 'יום ראשון',
    monday: 'יום שני',
    tuesday: 'יום שלישי',
    wednesday: 'יום רביעי',
    thursday: 'יום חמישי',
    friday: 'יום שישי',
    saturday: 'שבת'
};

function analyzeClientRow(row: unknown[], cols: Record<string, number>, ctx: { currentData: Customer[] }): ImportRowAnalysis<Customer> {
    const { name, idNum, externalIdStr, patch } = rowToPartial(row, cols);
    const errors: string[] = [];
    const warnings: string[] = [];

    if (!name && !idNum && !externalIdStr) {
        return { action: 'invalid', identity: {}, patch, errors: ['שורה ריקה או ללא מזהים'], warnings };
    }

    const working = ctx.currentData.filter((c) => Number(c.id) > -10);
    const existing = findClient(working, idNum, externalIdStr);

    if (!existing && !name) {
        return { action: 'invalid', identity: { idNum, externalIdStr }, patch, label: name, errors: ['חסר שם לקוח (נדרש ליצירת לקוח חדש)'], warnings };
    }

    if (existing) {
        const merged = applyPatch(existing, patch);
        if (!merged.name || merged.name.trim() === '') merged.name = existing.name;
        return {
            action: 'update',
            identity: { idNum, externalIdStr },
            label: existing.name || name,
            existing,
            patch,
            errors,
            warnings
        };
    }

    const createPatch: Record<string, unknown> = { ...patch, name };
    if (idNum && idNum > 0) createPatch.id = String(idNum);
    return {
        action: 'create',
        identity: { idNum, externalIdStr },
        label: name,
        patch: createPatch,
        errors,
        warnings
    };
}

export function buildClientsImportAdapter(dispatch: AppThunkDispatch): ImportAdapter<Customer> {
    return {
        entityLabel: 'לקוח',
        entityLabelPlural: 'לקוחות',
        title: 'טעינת לקוחות מאקסל',
        columns: CLIENTS_IMPORT_COLUMNS,
        fieldLabels: CLIENTS_FIELD_LABELS,
        maxRows: 5000,
        instructions: [
            'העלו קובץ אקסל/CSV שבו כל שורה מייצגת לקוח אחד. שורת הכותרת הראשונה צריכה להכיל את שמות העמודות.',
            'אם ל"מזהה" או ל"מזהה חיצוני" בשורה יש התאמה ללקוח קיים — השורה תעדכן אותו. אחרת תיווצר רשומת לקוח חדשה.',
            'שורות עם מזהה שחוזר פעמיים באותו קובץ נחסמות אוטומטית, כדי שלא יתבצע עדכון כפול על אותו לקוח בטעות.'
        ],
        analyzeRow: analyzeClientRow,
        commitCreate: async (patch) => {
            const p: Partial<Customer> = { ...patch };
            const created = makeCustomer(p as never);
            if (p.active === undefined) created.active = true;
            if (!created.name || !String(created.name).trim()) {
                return { ok: false, message: 'חסר שם לקוח (יצירה)' };
            }
            try {
                const after = (await dispatch(addCustomer(created as Customer)).unwrap()) as { first?: unknown } | undefined;
                const updatedEntity = after?.first ? deserializeCustomer(after.first) : undefined;
                return { ok: true, message: 'נוצר', updatedEntity };
            } catch (e) {
                return { ok: false, message: (e as Error)?.message || String(e) };
            }
        },
        commitUpdate: async (existing, patch) => {
            const { id: _idFromRow, ...rest } = patch as Record<string, unknown>;
            const merged = applyPatch(existing, rest as Partial<Customer>);
            if (!merged.name || merged.name.trim() === '') merged.name = existing.name;
            try {
                await dispatch(updateCustomer(merged)).unwrap();
                return { ok: true, message: 'עודכן', updatedEntity: merged };
            } catch (e) {
                return { ok: false, message: (e as Error)?.message || String(e) };
            }
        }
    };
}
